/**
 * What an item is while it is being written, as against what it is once it is saved.
 *
 * A form holds text, because that is what somebody types: half a number, a minus
 * sign on its own, an empty box they are about to fill. The definition the server
 * takes holds numbers. This is the piece in between, and keeping it here rather
 * than in the panel is what stops the panel being a thousand lines of parsing.
 */

import { SKILL_KEYS } from '@inverse/common';
import type { Act } from './objects';

export type ItemKind =
  'weapon' | 'armour' | 'ammunition' | 'consumable' | 'material' | 'quest' | 'other';

export const ITEM_KINDS: readonly ItemKind[] = [
  'weapon',
  'armour',
  'ammunition',
  'consumable',
  'material',
  'quest',
  'other',
];

/** What each kind is called where somebody reads it, and one line on what it is. */
export const KIND_LABELS: Readonly<Record<ItemKind, { one: string; many: string; about: string }>> =
  {
    weapon: {
      one: 'Weapon',
      many: 'Weapons',
      about: 'Held in the weapon hand. What it hits with and how fast.',
    },
    armour: {
      one: 'Armour',
      many: 'Armour',
      about: 'Worn somewhere. What it turns aside, and what it asks of whoever wears it.',
    },
    ammunition: {
      one: 'Ammunition',
      many: 'Ammunition',
      about: 'Spent to use something else: an arrow for a bow, a pinch of dust for a spell.',
    },
    consumable: {
      one: 'Consumable',
      many: 'Consumables',
      about: 'Eaten or drunk, and gone afterwards.',
    },
    material: {
      one: 'Material',
      many: 'Materials',
      about: 'Raw stuff, or a part of something else. Usually made into something.',
    },
    quest: {
      one: 'Quest item',
      many: 'Quest items',
      about: 'Carried for a reason. Never traded, and never dropped by accident.',
    },
    other: { one: 'Other', many: 'Other', about: 'Anything that is none of the above.' },
  };

export const EQUIPMENT_SLOTS = [
  'head',
  'cape',
  'neck',
  'weapon',
  'body',
  'shield',
  'legs',
  'hands',
  'feet',
  'ring',
  'ammunition',
] as const;

export type EquipmentSlot = (typeof EQUIPMENT_SLOTS)[number];

export const BONUS_FIELDS = [
  ['attackStab', 'Stab'],
  ['attackSlash', 'Slash'],
  ['attackBash', 'Bash'],
  ['attackRanged', 'Ranged'],
  ['attackMagic', 'Magic'],
  ['defenceStab', 'Stab'],
  ['defenceSlash', 'Slash'],
  ['defenceBash', 'Bash'],
  ['defenceRanged', 'Ranged'],
  ['defenceMagic', 'Magic'],
  ['strength', 'Strength'],
  ['rangedStrength', 'Ranged strength'],
  ['magicDamage', 'Magic damage'],
  // Kept under its old name in the file, because every item ever authored
  // carries it and renaming a field is a migration nobody asked for. What
  // anybody reads is the label.
  ['prayer', 'Faith'],
] as const;

export type BonusName = (typeof BONUS_FIELDS)[number][0];

export const REQUIREMENT_SKILLS = ['attack', 'strength', 'defence', 'ranged', 'magic'] as const;

/** Anything a recipe can train, which is anything at all. */
export const RECIPE_SKILLS = SKILL_KEYS;

/** An item as the form holds it. Everything a person types is a string. */
export interface ItemDraft {
  /** Zero for something that does not exist yet. */
  id: number;
  kind: ItemKind;
  name: string;
  examine: string;
  value: string;
  weight: string;
  stackable: boolean;
  tradeable: boolean;
  /** How many of it one person may hold at once, for a quest item. */
  questAtMost: string;
  /** Where another is to be had, said to somebody who destroys one. */
  questFoundAt: string;
  /** Menu entries while it is held, comma separated. */
  actions: string;
  /**
   * What each of those entries does.
   *
   * The same list scenery and characters carry, for the same reason: a scroll
   * read, a tablet broken, a potion drunk are things somebody writes rather
   * than things the world has built in.
   */
  acts: Act[];
  /**
   * What it is for, which is how the tools find it later.
   *
   * A hatchet tagged tool_woodcutting turns up where a tree asks what it needs,
   * and a fish tagged yield_fishing turns up in the list of what a spot holds.
   */
  tags: string[];
  model: string;
  heals: string;
  /** Filled in for anything that is worn, ignored for anything that is not. */
  slot: EquipmentSlot;
  twoHanded: boolean;
  speed: string;
  /** Ammunition worn in the ammunition slot, like an arrow, rather than kept in the bag like dust. */
  equips: boolean;
  /**
   * What a weapon fires, and the best of it that it can take. Empty for a
   * weapon that fires nothing.
   */
  firesKind: string;
  firesUpTo: string;
  /** What fires this, for ammunition, and where it sits on that ladder. */
  ammoKind: string;
  ammoTier: string;
  requirements: Record<string, string>;
  bonuses: Record<string, string>;
  recipe: RecipeDraft;
  /**
   * What it takes to gather this, and what gathering it gives.
   *
   * Kept on the item because a trout is worth the same wherever it is caught.
   * A level of nought means this is not something anybody gathers.
   */
  gatherLevel: string;
  gatherExperience: string;
  /**
   * How many of these come out of one spot before it is spent.
   *
   * On the material rather than on the spot: a pine gives an armful and an
   * ironwood gives one log and a sore back. Two numbers, because a spot that
   * always gives exactly four is a worse world than one that gives three to
   * five. The same number twice is the exact one.
   */
  chargesLeast: string;
  chargesMost: string;
  /**
   * What this is used in: the recipes it feeds rather than the one that makes it.
   *
   * A log is burned, an ore is smelted, a fish is cooked. All of those used to
   * be reachable only from whatever they produced, which is the wrong way round
   * for anything whose whole purpose is to be used up.
   */
  uses: UseDraft[];
}

/** One thing this item is used in, as the form holds it. */
export interface UseDraft {
  skill: (typeof RECIPE_SKILLS)[number];
  action: string;
  level: string;
  experience: string;
  /** Where it happens, or nought for something done in the bag. */
  objectId: string;
  /** What it turns into, or nought for something that is simply used up. */
  outputId: string;
  outputQuantity: string;
}

export interface RecipeDraft {
  /** Whether this item is made rather than merely found. */
  made: boolean;
  skill: (typeof RECIPE_SKILLS)[number];
  level: string;
  experience: string;
  /** The scenery it happens at, or zero for something done in the bag. */
  objectId: string;
  /** Anywhere else the same thing can be made: a stove as well as a fire. */
  alsoAt: string[];
  /** Whether the skill guide leaves it out. */
  hidden: boolean;
  action: string;
  inputId: string;
  inputQuantity: string;
  outputQuantity: string;
  /** What comes out when it goes wrong, or zero for nothing at all. */
  failureId: string;
  failureChance: string;
  interval: string;
  /** Other skills it takes, on top of the one it trains. */
  alsoNeeds: { skill: (typeof RECIPE_SKILLS)[number]; level: string }[];
  /**
   * What has to have happened before anybody may make it at all.
   *
   * A quest that teaches it, or a flag something else in the world writes. Left
   * empty, the recipe is known to everybody from the start.
   */
  lockQuest: string;
  lockAtLeast: string;
  lockFlag: string;
  lockFlagIs: string;
}

const NO_RECIPE: RecipeDraft = {
  made: false,
  skill: 'smithing',
  level: '1',
  experience: '10',
  objectId: '0',
  action: 'Make',
  inputId: '0',
  inputQuantity: '1',
  outputQuantity: '1',
  failureId: '0',
  failureChance: '0',
  interval: '4',
  alsoAt: [],
  hidden: false,
  alsoNeeds: [],
  lockQuest: '',
  lockAtLeast: '1',
  lockFlag: '',
  lockFlagIs: '',
};

/** A blank item of a kind, with the defaults that kind usually wants. */
/**
 * The tags the tools offer, grouped the way somebody thinks about them.
 *
 * Tags themselves are free text on the item; this is only what the tools put in
 * front of somebody. Two shapes are read by the pickers: `tool_<skill>` is what
 * a skill needs in hand, and `yield_<skill>` is what it produces.
 */
export const ITEM_TAGS: Readonly<Record<string, readonly string[]>> = {
  Tools: [
    'tool_woodcutting',
    'tool_mining',
    'tool_fishing',
    'tool_firemaking',
    'tool_cooking',
    'tool_smithing',
  ],
  // Fishing has four jobs rather than one, and the belt has a slot for each.
  Fishing: ['tool_fishing_net', 'tool_fishing_rod', 'tool_fishing_cage', 'tool_fishing_harpoon'],
  'What skills give': [
    'yield_woodcutting',
    'yield_mining',
    'yield_fishing',
    'yield_cooking',
    'yield_smithing',
  ],
  Fighting: ['weapon_melee', 'weapon_ranged', 'weapon_magic', 'armour', 'ammunition'],
  Other: ['food', 'material', 'currency', 'quest', 'junk'],
};

/** Every tag the tools suggest, flattened. */
export const ALL_ITEM_TAGS: readonly string[] = Object.values(ITEM_TAGS).flat();

/** The tag a kind of item almost always wants, so nothing starts untagged. */
const STARTING_TAGS: Readonly<Record<ItemKind, readonly string[]>> = {
  weapon: ['weapon_melee'],
  armour: ['armour'],
  ammunition: ['ammunition'],
  consumable: ['food'],
  material: ['material'],
  quest: ['quest'],
  other: [],
};

export function blankItem(kind: ItemKind): ItemDraft {
  return {
    id: 0,
    kind,
    name: '',
    examine: '',
    value: kind === 'quest' ? '0' : '10',
    weight: kind === 'weapon' ? '1.5' : '0.5',
    stackable: kind === 'material' || kind === 'ammunition',
    tradeable: kind !== 'quest',
    questAtMost: '1',
    questFoundAt: '',
    actions:
      kind === 'weapon' || kind === 'armour' || kind === 'ammunition'
        ? 'Wield'
        : kind === 'consumable'
          ? 'Eat'
          : '',
    acts: [],
    tags: STARTING_TAGS[kind] ? [...STARTING_TAGS[kind]] : [],
    model: 'placeholder',
    heals: kind === 'consumable' ? '3' : '0',
    slot: kind === 'weapon' ? 'weapon' : kind === 'ammunition' ? 'ammunition' : 'body',
    twoHanded: false,
    speed: '4',
    equips: kind === 'ammunition',
    firesKind: '',
    firesUpTo: '20',
    ammoKind: kind === 'ammunition' ? 'arrow' : '',
    ammoTier: '1',
    requirements: Object.fromEntries(REQUIREMENT_SKILLS.map((skill) => [skill, ''])),
    bonuses: Object.fromEntries(BONUS_FIELDS.map(([field]) => [field, '0'])),
    gatherLevel: '0',
    gatherExperience: '0',
    chargesLeast: '0',
    chargesMost: '0',
    uses: [],
    recipe: { ...NO_RECIPE },
  };
}

/** Whether a kind is worn, and so has a slot and bonuses worth asking about. */
export function isWorn(kind: ItemKind): boolean {
  return kind === 'weapon' || kind === 'armour';
}

/**
 * Whether this one is worn: anything a kind is always worn as, and ammunition
 * that goes in its slot rather than staying in the bag.
 */
export function wears(draft: Pick<ItemDraft, 'kind' | 'equips'>): boolean {
  return isWorn(draft.kind) || (draft.kind === 'ammunition' && draft.equips);
}

/** Reads a saved item back into the form. */
/**
 * What this item is used in, read off every recipe there is.
 *
 * Not sent with the item, because it is a fact about the recipes rather than
 * about the item, and the client already holds all of them. Reading it here
 * rather than asking for it keeps one truth.
 */
export function usesOf(
  itemId: number,
  recipes: readonly {
    objectId: number;
    action: string;
    skill: string;
    level: number;
    experience: number;
    inputId: number;
    outputId: number;
    outputQuantity: number;
  }[],
): UseDraft[] {
  return recipes
    .filter((one) => one.inputId === itemId)
    .map((one) => ({
      skill: one.skill as UseDraft['skill'],
      action: one.action,
      level: String(one.level),
      experience: String(one.experience),
      objectId: String(one.objectId),
      outputId: String(one.outputId),
      outputQuantity: String(one.outputQuantity || 1),
    }));
}

/**
 * What this item is used in, as the world wants it written.
 *
 * Nothing about the input is sent: the server fills that in from whichever item
 * is being saved, because an item's page saying it is used in something is the
 * item saying so.
 */
export function usesFrom(draft: ItemDraft): string {
  const number = (held: string, fallback = 0): number => {
    const value = Number(held);
    return Number.isFinite(value) ? value : fallback;
  };

  return JSON.stringify(
    draft.uses
      .filter((use) => use.action.trim().length > 0)
      .map((use) => ({
        objectId: Math.max(0, Math.round(number(use.objectId))),
        action: use.action.trim().slice(0, 24),
        skill: use.skill,
        level: Math.min(99, Math.max(1, Math.round(number(use.level, 1)))),
        experience: Math.max(0, number(use.experience)),
        inputQuantity: 1,
        outputId: Math.max(0, Math.round(number(use.outputId))),
        outputQuantity: Math.max(1, Math.round(number(use.outputQuantity, 1))),
      })),
  );
}

export function draftFrom(item: SavedItem, recipe: SavedRecipe | null): ItemDraft {
  const blank = blankItem(kindOfSaved(item));

  const requirements = { ...blank.requirements };
  for (const [skill, level] of Object.entries(item.equipment?.requirements ?? {})) {
    requirements[skill] = String(level);
  }

  const bonuses = { ...blank.bonuses };
  for (const [field] of BONUS_FIELDS) {
    const held = item.equipment?.bonuses?.[field];
    if (typeof held === 'number') bonuses[field] = String(held);
  }

  return {
    ...blank,
    id: item.id,
    kind: kindOfSaved(item),
    name: item.name,
    examine: item.examine ?? '',
    value: String(item.value ?? 0),
    weight: String(item.weight ?? 0),
    stackable: item.stackable === true,
    tradeable: item.tradeable !== false,
    questAtMost: String(item.quest?.atMost ?? 1),
    questFoundAt: item.quest?.foundAt ?? '',
    actions: (item.actions ?? []).join(', '),
    // Filled in rather than taken as found: an item written before acts had
    // every field carries none of them half set.
    acts: (item.acts ?? []).map((act) => ({
      on: act.on ?? 'menu',
      atHealth: act.atHealth ?? 50,
      action: act.action,
      when: act.when ?? [],
      does: act.does ?? [],
      refuses: act.refuses ?? '',
      hidden: act.hidden === true,
      once: 'always',
      doneSays: '',
    })),
    tags: [...(item.tags ?? blank.tags)],
    model: item.model ?? 'placeholder',
    heals: String(item.heals ?? 0),
    slot: (item.equipment?.slot ?? blank.slot) as EquipmentSlot,
    twoHanded: item.equipment?.twoHanded === true,
    speed: String(item.equipment?.speed ?? 4),
    equips: kindOfSaved(item) === 'ammunition' && item.equipment?.slot === 'ammunition',
    firesKind: item.equipment?.fires?.kind ?? '',
    firesUpTo: String(item.equipment?.fires?.upTo ?? 20),
    ammoKind: item.equipment?.ammunition?.kind ?? blank.ammoKind,
    ammoTier: String(item.equipment?.ammunition?.tier ?? 1),
    requirements,
    bonuses,
    gatherLevel: String(item.gathering?.level ?? 0),
    gatherExperience: String(item.gathering?.experience ?? 0),
    chargesLeast: String(item.gathering?.charges?.least ?? 0),
    chargesMost: String(item.gathering?.charges?.most ?? 0),
    uses: [],
    recipe: recipe
      ? {
          made: true,
          skill: recipe.skill,
          level: String(recipe.level),
          experience: String(recipe.experience),
          objectId: String(recipe.objectId),
          action: recipe.action,
          inputId: String(recipe.inputId),
          inputQuantity: String(recipe.inputQuantity),
          outputQuantity: String(recipe.outputQuantity),
          failureId: String(recipe.failureId ?? 0),
          failureChance: String(Math.round((recipe.failureChance ?? 0) * 100)),
          interval: String(recipe.interval),
          alsoAt: (recipe.alsoAt ?? []).map((one) => String(one)),
          hidden: recipe.hidden === true,
          alsoNeeds: (recipe.alsoNeeds ?? []).map((need) => ({
            skill: need.skill,
            level: String(need.level),
          })),
          lockQuest: recipe.locked?.quest ?? '',
          lockAtLeast: String(recipe.locked?.atLeast ?? 1),
          lockFlag: recipe.locked?.flag ?? '',
          lockFlagIs: recipe.locked?.is ?? '',
        }
      : { ...NO_RECIPE },
  };
}

/** An item as the server sends it back, which is the content file's own shape. */
export interface SavedItem {
  id: number;
  kind?: ItemKind;
  name: string;
  examine?: string;
  stackable?: boolean;
  tradeable?: boolean;
  /** What makes it a quest item, and how many of it anybody may hold. */
  quest?: { atMost: number; foundAt: string };
  value?: number;
  weight?: number;
  actions?: string[];
  tags?: string[];
  model?: string;
  heals?: number;
  /** What each menu entry does, loosely written, as a file may hold it. */
  acts?: (Partial<Act> & { action: string })[];
  /** What it takes to gather this, and what it gives. */
  gathering?: {
    level: number;
    experience: number;
    /** How many come out of one spot before it is spent. */
    charges?: { least: number; most: number };
  };
  equipment?: {
    slot: string;
    twoHanded?: boolean;
    speed?: number;
    requirements?: Record<string, number>;
    bonuses?: Record<string, number>;
    fires?: { kind: string; upTo: number };
    ammunition?: { kind: string; tier: number };
  };
}

export interface SavedRecipe {
  objectId: number;
  action: string;
  skill: (typeof RECIPE_SKILLS)[number];
  level: number;
  experience: number;
  inputId: number;
  inputQuantity: number;
  outputId: number;
  outputQuantity: number;
  failureId: number | null;
  failureChance: number;
  interval: number;
  /** Anywhere else the same thing can be made. */
  alsoAt?: number[];
  /** Whether the skill guide leaves it out. */
  hidden?: boolean;
  /** Other skills it takes, on top of the one it trains. */
  alsoNeeds?: { skill: (typeof RECIPE_SKILLS)[number]; level: number }[];
  /** What has to have happened before anybody may make it at all. */
  locked?: { quest?: string; atLeast?: number; flag?: string; is?: string };
}

/** The kind of a saved item, worked out the same way the server works it out. */
export function kindOfSaved(item: SavedItem): ItemKind {
  if (item.kind) return item.kind;
  if (item.equipment) {
    if (item.equipment.slot === 'ammunition') return 'ammunition';
    return item.equipment.slot === 'weapon' ? 'weapon' : 'armour';
  }
  // Said rather than guessed, the same as the server works it out. Reading
  // "nobody may trade it" as the mark of a quest item is true of most of them
  // and also true of a burnt fish, and the list called seven ruined suppers
  // quest items for as long as it did it that way.
  if (item.quest) return 'quest';
  if ((item.heals ?? 0) > 0) return 'consumable';
  return 'material';
}

const number = (text: string, fallback = 0): number => {
  const held = Number(text);
  return Number.isFinite(held) ? held : fallback;
};

/** The item the server is asked to save, built from what was typed. */
export function itemFrom(draft: ItemDraft): SavedItem {
  const actions = draft.actions
    .split(',')
    .map((action) => action.trim())
    .filter((action) => action.length > 0);

  const item: SavedItem = {
    id: draft.id,
    kind: draft.kind,
    name: draft.name.trim(),
    examine: draft.examine.trim(),
    stackable: draft.stackable,
    tradeable: draft.tradeable,
    value: Math.max(0, Math.round(number(draft.value))),
    weight: number(draft.weight),
    actions,
    // Written whole rather than a field at a time: an act is one thing.
    acts: draft.acts,
    tags: draft.tags.map((tag) => tag.trim().toLowerCase()).filter((tag) => tag.length > 0),
    model: draft.model.trim() || 'placeholder',
    heals: draft.kind === 'consumable' ? Math.max(0, Math.round(number(draft.heals))) : 0,
  };

  // Only on the ones that are one. Everything that follows from being a quest
  // item follows from this being here, so an item that stops being one stops
  // being capped, barred from shops and destroyed when dropped.
  if (draft.kind === 'quest') {
    item.quest = {
      atMost: Math.min(100, Math.max(1, Math.round(number(draft.questAtMost, 1)))),
      foundAt: draft.questFoundAt.trim(),
    };
  }

  // Left off entirely when nothing gathers it, so an item nobody catches does
  // not carry a level about with it.
  const gatherLevel = Math.round(number(draft.gatherLevel));
  if (gatherLevel > 0) {
    const least = Math.round(number(draft.chargesLeast));
    const most = Math.round(number(draft.chargesMost));

    item.gathering = {
      level: Math.min(99, Math.max(1, gatherLevel)),
      experience: Math.max(0, number(draft.gatherExperience)),
      // Left off entirely when nothing has been said, so a spot gives one and
      // is spent, which is what everything written before charges meant.
      ...(least > 0 || most > 0
        ? {
            charges: {
              least: Math.min(1000, Math.max(1, least || most)),
              most: Math.min(1000, Math.max(1, most || least)),
            },
          }
        : {}),
    };
  }

  if (wears(draft)) {
    const requirements: Record<string, number> = {};
    for (const [skill, level] of Object.entries(draft.requirements)) {
      const asked = Math.round(number(level));
      if (asked >= 1) requirements[skill] = Math.min(99, asked);
    }

    const bonuses: Record<string, number> = {};
    for (const [field] of BONUS_FIELDS) {
      bonuses[field] = Math.round(number(draft.bonuses[field] ?? '0'));
    }

    const firesKind = draft.firesKind.trim().toLowerCase();
    const ammoKind = draft.ammoKind.trim().toLowerCase();
    item.equipment = {
      slot:
        draft.kind === 'weapon'
          ? 'weapon'
          : draft.kind === 'ammunition'
            ? 'ammunition'
            : draft.slot,
      twoHanded: draft.kind === 'weapon' ? draft.twoHanded : false,
      speed:
        draft.kind === 'weapon'
          ? Math.min(10, Math.max(1, Math.round(number(draft.speed, 4))))
          : undefined,
      requirements,
      bonuses,
      ...(draft.kind === 'weapon' && firesKind
        ? {
            fires: {
              kind: firesKind.slice(0, 24),
              upTo: Math.min(20, Math.max(1, Math.round(number(draft.firesUpTo, 20)))),
            },
          }
        : {}),
      ...(draft.kind === 'ammunition' && ammoKind
        ? {
            ammunition: {
              kind: ammoKind.slice(0, 24),
              tier: Math.min(20, Math.max(1, Math.round(number(draft.ammoTier, 1)))),
            },
          }
        : {}),
    };
  }

  return item;
}

/** The recipe the server is asked to save, or an empty string for none. */
export function recipeFrom(draft: ItemDraft): string {
  const recipe = draft.recipe;
  if (!recipe.made) return '';

  const written: SavedRecipe = {
    objectId: Math.max(0, Math.round(number(recipe.objectId))),
    alsoAt: recipe.alsoAt
      .map((one) => Math.max(0, Math.round(number(one))))
      .filter((one) => one > 0),
    hidden: recipe.hidden,
    action: recipe.action.trim() || 'Make',
    skill: recipe.skill,
    level: Math.min(99, Math.max(1, Math.round(number(recipe.level, 1)))),
    experience: Math.max(0, number(recipe.experience)),
    inputId: Math.max(0, Math.round(number(recipe.inputId))),
    inputQuantity: Math.max(1, Math.round(number(recipe.inputQuantity, 1))),
    outputId: draft.id,
    outputQuantity: Math.max(1, Math.round(number(recipe.outputQuantity, 1))),
    failureId: Math.round(number(recipe.failureId)) || null,
    failureChance: Math.min(1, Math.max(0, number(recipe.failureChance) / 100)),
    interval: Math.min(20, Math.max(1, Math.round(number(recipe.interval, 4)))),
    alsoNeeds: recipe.alsoNeeds
      .filter((need) => Math.round(number(need.level, 1)) > 1)
      .map((need) => ({
        skill: need.skill,
        level: Math.min(99, Math.max(1, Math.round(number(need.level, 1)))),
      })),
    // Left out entirely when nothing gates it, so a recipe everybody knows does
    // not carry an empty gate about with it.
    locked:
      recipe.lockQuest.trim() || recipe.lockFlag.trim()
        ? {
            ...(recipe.lockQuest.trim() ? { quest: recipe.lockQuest.trim() } : {}),
            ...(recipe.lockQuest.trim()
              ? { atLeast: Math.max(0, Math.round(number(recipe.lockAtLeast, 1))) }
              : {}),
            ...(recipe.lockFlag.trim() ? { flag: recipe.lockFlag.trim() } : {}),
            ...(recipe.lockFlag.trim() && recipe.lockFlagIs.trim()
              ? { is: recipe.lockFlagIs.trim() }
              : {}),
          }
        : undefined,
  };

  return JSON.stringify(written);
}

/** What is wrong with a draft, or nothing when it is ready to be saved. */
export function problemWith(draft: ItemDraft): string | null {
  if (draft.name.trim().length === 0) return 'It needs a name.';
  if (draft.name.trim().length > 48) return 'That name is too long.';
  if (draft.examine.length > 160) return 'That examine text is too long.';
  if (draft.kind === 'consumable' && Math.round(Number(draft.heals) || 0) <= 0) {
    return 'Something eaten should put back at least one point of health.';
  }
  if (draft.recipe.made && Math.round(Number(draft.recipe.inputId) || 0) <= 0) {
    return 'A recipe needs something to make it from.';
  }
  return null;
}

/** One line of the list the server sends: an item, sorted by kind. */
export interface ListedItem {
  id: number;
  kind: ItemKind;
  name: string;
}

/** One item in full, as the tools hold it while it is being looked at. */
export interface ItemDetail {
  item: SavedItem;
  recipe: SavedRecipe | null;
  /** What an ordinary shop would charge and pay for it. */
  buys: string;
  sells: string;
}

/** Reads the list the server sends: one line per item, id, kind and name. */
export function readItemList(written: string): ListedItem[] {
  const found: ListedItem[] = [];

  for (const line of written.split('\n')) {
    const [id, kind, ...rest] = line.split('|');
    if (!id || !kind || rest.length === 0) continue;
    found.push({ id: Number(id), kind: kind as ItemKind, name: rest.join('|') });
  }

  return found;
}

/** Reads one item out of what the server answered, or nothing if it is not readable. */
export function readItemDetail(packet: { text: Record<string, string> }): ItemDetail | null {
  try {
    const item = JSON.parse(packet.text.json ?? '') as SavedItem;
    const recipe = packet.text.recipe ? (JSON.parse(packet.text.recipe) as SavedRecipe) : null;
    return { item, recipe, buys: packet.text.buys ?? '', sells: packet.text.sells ?? '' };
  } catch {
    return null;
  }
}
